import {
    ElementRef,
    Injectable,
    WritableSignal,
    signal,
} from '@angular/core';
import { first } from 'lodash';

import { mapStylesMC, mcMapFeatureStyles } from './models';
import { McMciDataNode, IDatiqMapGeoJsonFeature } from '../../models';

@Injectable({
    providedIn: 'any',
})
export class MapService {
    private _map: WritableSignal<google.maps.Map> = signal(null);
    private _infoWindow: google.maps.InfoWindow;

    constructor() {}

    async loadMciFeatures(
        areaFeatures: IDatiqMapGeoJsonFeature,
        mciData: McMciDataNode[],
    ): Promise<google.maps.Data> {
        const map = this._map();
        const areas: google.maps.Data = new google.maps.Data();

        this.initializeFeatures(areas, areaFeatures, mciData);
        this.initializeStyles(areas, mciData);
        this.createInteractions(areas, map);
        areas.setMap(map);

        return areas;
    }

    private createInteractions(
        areas: google.maps.Data,
        map: google.maps.Map,
    ): void {
        areas.addListener('mouseover', (event) => {
            const feature = event.feature;
            const infoWindow = this.generateInfoWindow();
            const toolTipAnchor = this._getToolTipCenter(
                event.feature.getGeometry(),
            );
            const title = feature.getProperty('title');
            const subTitle = feature.getProperty('subTitle');

            infoWindow.setContent(`<div class="flex flex-col text-center items-center text-xs !m-0 !p-0"> 
                  <h1>${title}</h1>
                  <h2 class="font-bold">${subTitle}</h2>
                </div>`);

            infoWindow.setPosition(toolTipAnchor);
            infoWindow.open(map);
        });

        areas.addListener('mouseout', (event) => {
            const feature = event.feature;
            const infoWindow = this.generateInfoWindow();

            infoWindow.close();
        });
    }

    private _getToolTipCenter(geometry: google.maps.Data.Geometry) {
        const bounds = new google.maps.LatLngBounds();
        geometry.forEachLatLng((latLng) => {
            bounds.extend(latLng);
        });

        return this._getCenterFromBounds(bounds);
    }

    private _getCenterFromBounds(bounds: google.maps.LatLngBounds) {
        return new google.maps.LatLng({
            lat: bounds.getNorthEast().lat(),
            lng: bounds.getCenter().lng(),
        });
    }

    private initializeStyles(
        areas: google.maps.Data,
        mciData: {
            geoCode?: string | number;
            mci?: number;
            label?: string;
            color?: string;
            capacity?: string;
        }[],
    ) {
        areas.setStyle({
            opacity: 0,
        });
        areas.setStyle((feature): google.maps.Data.StyleOptions => {
            const code = feature.getProperty('code');
            const mciNode: McMciDataNode = first(
                mciData.filter((mci) => mci.geoCode === code),
            );

            if (!mciNode) {
                return {
                    ...mcMapFeatureStyles,
                    fillOpacity: 0,
                    strokeWeight: 0,
                };
            }

            return {
                ...mcMapFeatureStyles,
                fillColor: mciNode.color,
                fillOpacity: 0.6,
                strokeWeight: 2,
                strokeColor: '#fff',
            };
        });
    }

    private initializeFeatures(
        areas: google.maps.Data,
        areaFeatures: IDatiqMapGeoJsonFeature,
        mciData: {
            geoCode?: string | number;
            mci?: number;
            label?: string;
            color?: string;
            capacity?: string;
        }[],
    ) {
        areas.addGeoJson(areaFeatures).forEach((feature) => {
            const code = feature.getProperty('code');
            const mciNode: McMciDataNode = first(
                mciData.filter((mci) => mci.geoCode === code),
            );

            if (!mciNode) return;

            const title = mciNode?.label;
            const subTitle = `${mciNode?.mci} ${mciNode.capacity}`;

            feature.setProperty('id', code);
            feature.setProperty('title', title);
            feature.setProperty('subTitle', subTitle);
            feature.setProperty('color', mciNode.color);
            feature.setProperty('capacity', mciNode.capacity);
            feature.setProperty('mci', mciNode.mci);
        });
    }

    async load(ref: ElementRef, zoom: number = 4): Promise<google.maps.Map> {
        const map = new google.maps.Map(
            ref.nativeElement.querySelector('[data-uitype="map"]'),
            {
                disableDefaultUI: true,
                styles: mapStylesMC,
                zoomControl: true,
                center: { lat: 39, lng: -98 },
                zoom,
                minZoom: 3,
                fullscreenControl: true,
                controlSize: 24,
                fullscreenControlOptions: {
                    position: google.maps.ControlPosition.BOTTOM_RIGHT,
                },
            },
        );

        this._map.set(map);

        return map;
    }

    private calculateMidPoint(
        origin: { lat: number; lng: number },
        destination: { lat: number; lng: number },
    ): { lat: number; lng: number } {
        const midLat = (origin.lat + destination.lat) / 2;
        const midLng = (origin.lng + destination.lng) / 2;
        return { lat: midLat, lng: midLng };
    }

    private generateInfoWindow(): google.maps.InfoWindow {
        if (!this._infoWindow) {
            this._infoWindow = new google.maps.InfoWindow({
                disableAutoPan: true,
                maxWidth: 150,
            });

            this._infoWindow.set('headerDisabled', true);
        }

        return this._infoWindow;
    }
}
